import { env } from "@/configs/env";
import { requestor } from "@/libs/api-client";
import { CancelledError } from "@tanstack/react-query";
import { useUnmount } from "ahooks";
import { produce } from "immer";
import { useCallback, useRef, useState } from "react";
import * as tus from "tus-js-client";
import type { SetOptional } from "type-fest";
import { z } from "zod";

/**
 * @file use-upload.ts
 * @description A React hook for handling file uploads using the TUS resumable upload protocol.
 * This hook provides a complete solution for file uploads with progress tracking, error handling,
 * and resumable uploads functionality. It leverages tus-js-client for the underlying upload mechanism.
 */

/**
 * Schema for a completed file entity after successful upload
 * Used for validation and type inference
 */
export const fileEntitySchema = z.object({
  id: z.string(),
  mimeType: z.string(),
  size: z.number(),
  name: z.string(),
  isRemote: z.boolean(),
  source: z.string(),
  uploadUrl: z.string().url(),
});

export type FileEntity = z.infer<typeof fileEntitySchema>;

/**
 * Represents a file that is currently in the process of being uploaded
 * Includes additional properties for tracking upload progress and showing previews
 */
export type FileEntityUploading = SetOptional<
  FileEntity,
  "isRemote" | "source" | "uploadUrl"
> & {
  previewUrl: string; // URL for previewing the file (revoked after upload completes/fails)
  progress: number; // Upload progress percentage (0-100)
  error?: string; // Error message if upload failed
};

/**
 * Internal status tracking for the upload process
 */
interface UploadStatus {
  isUploading: boolean; // Whether an upload is currently in progress
  isSuccess: boolean; // Whether the most recent upload completed successfully
  isError: boolean; // Whether the most recent upload failed
  progress: number; // Current upload progress percentage (0-100)
  error: Error | null; // Error object if upload failed
}

/**
 * Configuration options for the useUpload hook
 */
interface UseUploadOptions {
  onUploadIdAvailable?: (uploadId: string) => void; // Called when the upload ID is available from TUS
  onSuccess?: (fileEntity: FileEntity) => void; // Called on successful upload completion
  onError?: (error: Error) => void; // Called when an upload error occurs
}

/**
 * Options that can be passed to the upload function directly
 * Provides per-upload callbacks without overriding global settings
 */
interface UploadOptions {
  onUploadIdAvailable?: (uploadId: string) => void;
  onSuccess?: (fileEntity: FileEntity) => void;
  onError?: (error: Error) => void;
  onProgress?: (progress: number) => void;
}

/**
 * Return type for the useUpload hook
 */
interface UseUploadResult {
  upload: (file: File, options?: UploadOptions) => void; // Function to start uploading a file with optional per-upload callbacks
  cancel: () => void; // Function to cancel the current upload
  reset: () => void; // Function to reset the upload state
  isUploading: boolean; // Whether an upload is in progress
  isSuccess: boolean; // Whether the upload completed successfully
  isError: boolean; // Whether the upload failed
  error: Error | null; // Error object if upload failed
  progress: number; // Current upload progress (0-100)
  fileEntity: FileEntity | null; // The completed file entity if upload succeeded
  fileEntityUploading: FileEntityUploading | null; // The uploading file entity during upload
}

/**
 * React hook for handling file uploads using the TUS protocol
 *
 * Features:
 * - Progress tracking
 * - Upload cancellation
 * - Automatic retry with configurable delays
 * - File preview URLs
 * - Resumable uploads
 * - TypeScript type safety
 * - Per-upload callbacks
 *
 * @param options - Configuration options for the upload behavior
 * @returns Object containing upload state and control functions
 */
export function useUpload({
  onUploadIdAvailable: globalOnUploadIdAvailable,
  onSuccess: globalOnSuccess,
  onError: globalOnError,
}: UseUploadOptions = {}): UseUploadResult {
  // Combine related state into a single status object
  const [status, setStatus] = useState<UploadStatus>({
    isUploading: false,
    isSuccess: false,
    isError: false,
    progress: 0,
    error: null,
  });
  const [fileEntity, setFileEntity] = useState<FileEntity | null>(null);
  const [fileEntityUploading, setFileEntityUploading] =
    useState<FileEntityUploading | null>(null);

  // Refs to store mutable values that persist across renders
  const abortControllerRef = useRef<AbortController | null>(null);
  const fileRef = useRef<File | null>(null);
  const uploadInstanceRef = useRef<tus.Upload | null>(null);

  /**
   * Helper function to update the status state with partial updates
   * Prevents redundant state setting for unchanged values
   */
  const updateStatus = useCallback((updates: Partial<UploadStatus>) => {
    setStatus((prev) => ({ ...prev, ...updates }));
  }, []);

  /**
   * Handles upload progress events from the TUS client
   * Updates both the status and fileEntityUploading with the current progress
   */
  const handleProgress = useCallback(
    (
      bytesUploaded: number,
      bytesTotal: number | null,
      localOnProgress?: (calculatedProgress: number) => void,
    ) => {
      // Calculate progress percentage, default to 0 if bytesTotal is not available
      const calculatedProgress = bytesTotal
        ? Math.round((bytesUploaded / bytesTotal) * 100)
        : 0;

      updateStatus({ progress: calculatedProgress });

      setFileEntityUploading(
        produce((draft) => {
          if (draft) {
            draft.progress = calculatedProgress;
          }
        }),
      );

      // Invoke callbacks if provided
      if (localOnProgress) {
        localOnProgress(calculatedProgress);
      }
    },
    [updateStatus],
  );

  /**
   * Handles error events from the upload process
   * Updates states and invokes onError callback if provided
   */
  const handleError = useCallback(
    (err: Error, localOnError?: (error: Error) => void) => {
      // Special handling for cancellation - don't treat it as an error
      if (err instanceof CancelledError) {
        updateStatus({ isUploading: false });
        return;
      }

      // Update error states
      updateStatus({
        error: err,
        isError: true,
        isUploading: false,
      });

      // Update the uploading entity with the error message
      setFileEntityUploading(
        produce((draft) => {
          if (draft) {
            draft.error = err.message;
          }
        }),
      );

      // Invoke callbacks if provided
      if (localOnError) {
        localOnError(err);
      }

      if (globalOnError) {
        globalOnError(err);
      }
    },
    [globalOnError, updateStatus],
  );

  /**
   * Cleans up the preview URL to prevent memory leaks
   * Should be called when upload completes or component unmounts
   */
  const cleanupPreviewUrl = useCallback(() => {
    if (fileEntityUploading?.previewUrl) {
      URL.revokeObjectURL(fileEntityUploading.previewUrl);
    }
  }, [fileEntityUploading]);

  /**
   * Cancels an ongoing upload
   * Aborts the TUS client and cleans up resources
   */
  const cancel = useCallback(() => {
    // Abort any pending operations
    if (abortControllerRef.current) {
      abortControllerRef.current.abort("Upload cancelled by user");
      abortControllerRef.current = null;
    }

    updateStatus({ isUploading: false });
    cleanupPreviewUrl();
  }, [updateStatus, cleanupPreviewUrl]);

  /**
   * Main function to start uploading a file
   * Sets up the TUS client and handles the upload process
   * @param file The file to upload
   * @param options Optional callbacks for this specific upload
   */
  const upload = useCallback(
    (file: File, options?: UploadOptions) => {
      // Cancel any ongoing upload before starting a new one
      if (status.isUploading) {
        cancel();
      }

      // Store the file reference and create a new abort controller
      fileRef.current = file;
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Reset state for new upload
      updateStatus({
        isUploading: true,
        isSuccess: false,
        isError: false,
        error: null,
        progress: 0,
      });
      setFileEntity(null);

      // Create initial FileEntityUploading with preview URL
      const previewUrl = URL.createObjectURL(file);
      const initialFileEntityUploading: FileEntityUploading = {
        id: "", // Will be updated when uploadId is available
        mimeType: file.type,
        size: file.size,
        name: file.name,
        previewUrl,
        progress: 0,
      };
      setFileEntityUploading(initialFileEntityUploading);

      // Check if already aborted before starting
      if (abortController.signal.aborted) {
        handleError(new CancelledError({ silent: true }), options?.onError);
        return;
      }

      // Create and configure TUS upload instance
      const uploadInstance = new tus.Upload(file, {
        endpoint: env.PUB_TUS_ENDPOINT,
        // Configure retry delays - different for dev and production
        retryDelays: import.meta.env.DEV ? [0] : [0, 3000, 5000],
        chunkSize: 100 * 1024, // 100KB chunk size
        metadata: {
          filename: file.name,
          filetype: file.type,
        },
        // Called when the upload URL is available - typically early in the process
        onUploadUrlAvailable: () => {
          const uploadId = uploadInstance.url?.split("/").pop();
          if (uploadId) {
            // Update the uploading entity with the ID
            setFileEntityUploading(
              produce((draft) => {
                if (draft) {
                  draft.id = uploadId;
                }
              }),
            );
            // Notify via callbacks if provided
            if (options?.onUploadIdAvailable) {
              options.onUploadIdAvailable(uploadId);
            }
            if (globalOnUploadIdAvailable) {
              globalOnUploadIdAvailable(uploadId);
            }
          }
        },
        onError: (err) => handleError(err, options?.onError),
        onProgress: (bytesUploaded, bytesTotal) =>
          handleProgress(bytesUploaded, bytesTotal, options?.onProgress),
        // Called when upload completes successfully
        onSuccess: () => {
          if (!uploadInstance.url) {
            handleError(
              new Error("Upload failed: no URL returned"),
              options?.onError,
            );
            return;
          }

          // Extract upload ID from the URL
          const uploadId = uploadInstance.url.split("/").pop();
          if (!uploadId) {
            handleError(
              new Error(
                "Upload failed: unable to extract upload ID, possibly due to incompatible server",
              ),
              options?.onError,
            );
            return;
          }

          // Create the final FileEntity
          const entity: FileEntity = {
            id: uploadId,
            mimeType: file.type,
            size: file.size,
            name: file.name,
            isRemote: true,
            source: "tus",
            uploadUrl: uploadInstance.url,
          };

          // Update states to reflect successful completion
          setFileEntity(entity);
          updateStatus({
            isSuccess: true,
            isUploading: false,
            isError: false,
          });

          // Invoke success callbacks if provided
          if (options?.onSuccess) {
            options.onSuccess(entity);
          }
          if (globalOnSuccess) {
            globalOnSuccess(entity);
          }
        },
      });

      uploadInstanceRef.current = uploadInstance;

      // Set up abort handler for cancellation
      abortController.signal.addEventListener("abort", () => {
        uploadInstance.abort(true);
        handleError(new CancelledError({ silent: true }), options?.onError);
      });

      // Check for previous uploads to enable resuming
      uploadInstance.findPreviousUploads().then((previousUploads) => {
        if (previousUploads.length) {
          // Resume from the most recent upload
          uploadInstance.resumeFromPreviousUpload(previousUploads[0]);
        }
        // Start the upload process
        uploadInstance.start();
      });
    },
    [
      status.isUploading,
      handleError,
      handleProgress,
      globalOnUploadIdAvailable,
      globalOnSuccess,
      updateStatus,
      cancel,
    ],
  );

  /**
   * Completely resets the upload state
   * Cancels any active upload and clears all state
   */
  const reset = useCallback(() => {
    // Cancel active upload if necessary
    if (status.isUploading) {
      cancel();
    }

    // Reset all state to initial values
    updateStatus({
      isUploading: false,
      isSuccess: false,
      isError: false,
      error: null,
      progress: 0,
    });

    setFileEntity(null);
    cleanupPreviewUrl();
    setFileEntityUploading(null);

    // Clear refs
    fileRef.current = null;
    uploadInstanceRef.current = null;
  }, [status.isUploading, cancel, cleanupPreviewUrl, updateStatus]);

  // Cleanup effect when component unmounts
  useUnmount(() => {
    // Ensure uploads are aborted and resources are cleaned up
    if (abortControllerRef.current) {
      abortControllerRef.current.abort("Component unmounted");
    }

    cleanupPreviewUrl();
  });

  // Return the complete hook API
  return {
    upload,
    cancel,
    reset,
    ...status,
    fileEntity,
    fileEntityUploading,
  };
}

export async function loadFileFromFileEntity(
  fileEntity: FileEntity,
  signal?: AbortSignal,
) {
  const blob = await requestor.get(fileEntity.uploadUrl, { signal }).blob();
  return new File([blob], fileEntity.name, { type: fileEntity.mimeType });
}
